import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton, Chip, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import { formatDisplayName } from '../../../../../../../../utils/formatters'
import ColumnConfigEditor from './ColumnConfigEditor'

function PresentationTableConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)
  const [newVariationGroup, setNewVariationGroup] = useState('')
  const [showVariationInput, setShowVariationInput] = useState(false)

  // Sync local config when prop changes (e.g., selecting a different presentation)
  useEffect(() => {
    setLocalConfig(config)
  }, [config])

  // Notify parent of changes
  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const footerPurposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'None', value: '' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const selectedPurposeColumns = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) {
      return []
    }
    return allPurposes[localConfig.purposeCode].columns || []
  }, [localConfig.purposeCode, allPurposes])

  const footerPurposeColumns = useMemo(() => {
    if (!localConfig.footerPurposeCode || !allPurposes[localConfig.footerPurposeCode]) {
      return []
    }
    return allPurposes[localConfig.footerPurposeCode].columns || []
  }, [localConfig.footerPurposeCode, allPurposes])

  const handlePurposeChange = useCallback((purposeCode) => {
    const purpose = allPurposes[purposeCode]
    if (purpose && purpose.columns) {
      // Pre-fill columns from the selected purpose with formatted display names
      const prefillColumns = purpose.columns.map(col => ({
        name: col.name,
        displayName: formatDisplayName(col.name),
        description: col.description || null,
        formatString: null,
        align: ['integer', 'decimal'].includes(col.dataType) ? 'right' : 'left',
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        hidden: col.hidden || false,
        exclude: false
      }))
      setLocalConfig(prev => ({
        ...prev,
        purposeCode,
        columns: prefillColumns
      }))
    } else {
      setLocalConfig(prev => ({
        ...prev,
        purposeCode: purposeCode || null,
        columns: []
      }))
    }
  }, [allPurposes])

  const handleAddColumn = useCallback((column) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: [...prev.columns, column]
    }))
  }, [])

  const handleRemoveColumn = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateColumn = useCallback((index, updated) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? updated : col)
    }))
  }, [])

  const handleAddSortField = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: [...(prev.defaultSort || []), { field: '', dir: 'asc' }]
    }))
  }, [])

  const handleRemoveSortField = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateSortField = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.map((sort, i) =>
        i === index ? { ...sort, [field]: value } : sort
      )
    }))
  }, [])

  const sortFieldOptions = useMemo(() => {
    return localConfig.columns.map(col => ({
      label: col.displayName || col.name,
      value: col.name
    }))
  }, [localConfig.columns])

  const sortDirOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' }
  ]

  const handleAddVariationGroup = useCallback(() => {
    const trimmed = newVariationGroup.trim().toLowerCase().replace(/\s+/g, '-')
    if (!trimmed) return
    if ((localConfig.variationGroups || []).includes(trimmed)) {
      setNewVariationGroup('')
      setShowVariationInput(false)
      return
    }
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: [...(prev.variationGroups || []), trimmed]
    }))
    setNewVariationGroup('')
    setShowVariationInput(false)
  }, [newVariationGroup, localConfig.variationGroups])

  const handleRemoveVariationGroup = useCallback((groupToRemove) => {
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: (prev.variationGroups || []).filter(g => g !== groupToRemove)
    }))
  }, [])

  const handleFooterPurposeChange = useCallback((footerPurposeCode) => {
    setLocalConfig(prev => ({
      ...prev,
      footerPurposeCode: footerPurposeCode || null,
      footerColumnMapping: [] // Clear mappings when changing footer purpose
    }))
  }, [])

  // Get the footer column mapped to a main column (if any)
  const getFooterColumnForMain = useCallback((mainColumnName) => {
    const mapping = (localConfig.footerColumnMapping || []).find(m => m.mainColumn === mainColumnName)
    return mapping?.footerColumn || ''
  }, [localConfig.footerColumnMapping])

  // Update the footer column for a specific main column
  const handleFooterMappingChange = useCallback((mainColumnName, footerColumnName) => {
    setLocalConfig(prev => {
      // Remove any existing mapping for this main column
      let newMappings = (prev.footerColumnMapping || []).filter(m => m.mainColumn !== mainColumnName)

      // Add new mapping if a footer column was selected
      if (footerColumnName) {
        newMappings = [...newMappings, { mainColumn: mainColumnName, footerColumn: footerColumnName }]
      }

      return {
        ...prev,
        footerColumnMapping: newMappings
      }
    })
  }, [])

  // Auto-configure: match columns by name
  const handleAutoConfigureFooterMapping = useCallback(() => {
    const footerColumnNames = new Set(footerPurposeColumns.map(c => c.name))
    const newMappings = (localConfig.columns || [])
      .filter(col => footerColumnNames.has(col.name))
      .map(col => ({ mainColumn: col.name, footerColumn: col.name }))

    setLocalConfig(prev => ({
      ...prev,
      footerColumnMapping: newMappings
    }))
  }, [localConfig.columns, footerPurposeColumns])

  // Clear all footer mappings
  const handleClearFooterMappings = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      footerColumnMapping: []
    }))
  }, [])

  const footerColumnSelectOptions = useMemo(() => {
    return [
      { label: '(None)', value: '' },
      ...footerPurposeColumns.map(col => ({
        label: col.name,
        value: col.name
      }))
    ]
  }, [footerPurposeColumns])

  // Count how many columns have mappings
  const mappedColumnCount = useMemo(() => {
    return (localConfig.footerColumnMapping || []).length
  }, [localConfig.footerColumnMapping])

  return (
    <Grid container spacing={2}>
      {/* Data Purpose Selection */}
      <Grid item xs={12}>
        <SydLabel required label='Data Configuration Purpose' helperText='Select which data purpose to display'>
          <SydSelect
            options={purposeOptions}
            value={localConfig.purposeCode || ''}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Latch Date Mode - only show when selected purpose has latch config */}
      {localConfig.purposeCode && allPurposes[localConfig.purposeCode]?.latch
        ? (
          <Grid item xs={12}>
            <SydLabel label='Latch Date Mode' helperText='How end users interact with the snapshot date'>
              <SydSelect
                options={[
                  { label: 'User Selectable', value: 'selectable' },
                  { label: 'Latest Only (read-only)', value: 'latest' }
                ]}
                value={localConfig.latchMode || 'selectable'}
                onChange={(e) => setLocalConfig(prev => ({ ...prev, latchMode: e.target.value }))}
              />
            </SydLabel>
          </Grid>
        )
        : null}

      {/* Column Configuration */}
      <Grid item xs={12}>
        <ColumnConfigEditor
          columns={localConfig.columns || []}
          availableColumns={selectedPurposeColumns}
          onAddColumn={handleAddColumn}
          onRemoveColumn={handleRemoveColumn}
          onUpdateColumn={handleUpdateColumn}
        />
      </Grid>

      {/* Footer Purpose (Optional) */}
      <Grid item xs={12}>
        <SydLabel label='Footer Data Purpose' helperText='Optional purpose for footer/summary data'>
          <SydSelect
            options={footerPurposeOptions}
            value={localConfig.footerPurposeCode || ''}
            onChange={(e) => handleFooterPurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Footer Column Mapping - only show when footer purpose is selected */}
      {localConfig.footerPurposeCode
        ? (
          <Grid item xs={12}>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h4 style={{ margin: 0 }}>Footer Column Mapping</h4>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                ({mappedColumnCount} of {(localConfig.columns || []).length} mapped)
              </span>
              <SydButton
                size='sm'
                icon='sync'
                onClick={handleAutoConfigureFooterMapping}
                disabled={(localConfig.columns || []).length === 0}
              >
                Auto-Configure
              </SydButton>
              {mappedColumnCount > 0
                ? (
                  <SydButton
                    size='sm'
                    variant='ghost'
                    onClick={handleClearFooterMappings}
                  >
                    Clear All
                  </SydButton>
                )
                : null}
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '12px' }}>
              Map main columns to footer columns for alignment. Auto-Configure matches columns with the same name.
            </p>

            {(localConfig.columns || []).length > 0 ? (
              <Table className='opal-presentation-table zebra-rows' size='small'>
                <TableHead className='opt-thead'>
                  <TableRow className='opt-tr'>
                    <TableCell className='opt-th' style={{ width: '50%' }}>Main Column</TableCell>
                    <TableCell className='opt-th' style={{ width: '50%' }}>Footer Column</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className='opt-tbody'>
                  {(localConfig.columns || []).map((col) => (
                    <TableRow className='opt-tr' key={col.name}>
                      <TableCell className='opt-td' style={{ fontWeight: 500 }}>
                        {col.displayName || col.name}
                        {col.displayName && col.displayName !== col.name
                          ? (
                            <span style={{ color: 'var(--text-color-subtle)', marginLeft: '8px', fontSize: '0.75rem' }}>
                              ({col.name})
                            </span>
                          )
                          : null}
                      </TableCell>
                      <TableCell className='opt-td'>
                        <SydSelect
                          options={footerColumnSelectOptions}
                          value={getFooterColumnForMain(col.name)}
                          onChange={(e) => handleFooterMappingChange(col.name, e.target.value)}
                          size='sm'
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: 0 }}>
                No columns configured. Select a data purpose first.
              </p>
            )}
          </Grid>
        )
        : null}

      {/* Default Sort */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Default Sort Order</h4>
          <SydButton
            size='sm'
            icon='add'
            onClick={handleAddSortField}
            disabled={localConfig.columns.length === 0}
          >
            Add Sort
          </SydButton>
        </div>
        {(localConfig.defaultSort || []).map((sort, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <SydSelect
                options={sortFieldOptions}
                value={sort.field}
                onChange={(e) => handleUpdateSortField(index, 'field', e.target.value)}
                placeholder='Select field'
              />
            </div>
            <div>
              <SydSelect
                options={sortDirOptions}
                value={sort.dir}
                onChange={(e) => handleUpdateSortField(index, 'dir', e.target.value)}
              />
            </div>
            <IconButton size='small' onClick={() => handleRemoveSortField(index)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </div>
        ))}
        {(localConfig.defaultSort || []).length === 0
          ? (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: 0 }}>
              No default sort configured
            </p>
          )
          : null}
      </Grid>

      {/* Variation Groups */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Variation Groups</h4>
          {!showVariationInput
            ? (
              <SydButton
                size='sm'
                icon='add'
                onClick={() => setShowVariationInput(true)}
              >
                Add Group
              </SydButton>
            )
            : null}
        </div>

        {showVariationInput
          ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <SydInput
                value={newVariationGroup}
                onChange={(e) => setNewVariationGroup(e.target.value)}
                placeholder='Enter group name'
                style={{ maxWidth: '200px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddVariationGroup()
                  }
                  if (e.key === 'Escape') {
                    setShowVariationInput(false)
                    setNewVariationGroup('')
                  }
                }}
                autoFocus
              />
              <SydButton size='sm' variant='primary' onClick={handleAddVariationGroup} disabled={!newVariationGroup.trim()}>
                Add
              </SydButton>
              <SydButton size='sm' variant='ghost' onClick={() => { setShowVariationInput(false); setNewVariationGroup('') }}>
                Cancel
              </SydButton>
            </div>
          )
          : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {(localConfig.variationGroups || []).map((group) => (
            <Chip
              key={group}
              label={group}
              onDelete={() => handleRemoveVariationGroup(group)}
              variant='outlined'
              size='small'
            />
          ))}
          {(localConfig.variationGroups || []).length === 0
            ? (
              <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
                No variation groups configured
              </span>
            )
            : null}
        </div>
      </Grid>

      {/* Local Storage Key */}
      <Grid item xs={12}>
        <SydLabel label='Local Storage Key' helperText='Unique key for storing user preferences'>
          <SydInput
            value={localConfig.localStorageKey || ''}
            onChange={(e) => setLocalConfig(prev => ({
              ...prev,
              localStorageKey: e.target.value
            }))}
          />
        </SydLabel>
      </Grid>
    </Grid>
  )
}

PresentationTableConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default PresentationTableConfigEditor
